import { FormEvent, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { format, addDays } from 'date-fns';

import { Layout } from '@components/Layout';
import { Card } from '@components/Card';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { useAuth } from '@hooks/useAuth';
import { useCreateBooking, useBikeAvailability, useCheckAvailability } from '@hooks/useBookings';
import { getBike } from '@lib/firebase/bikes';
import { getLocation } from '@lib/firebase/locations';
import { formatCurrency } from '@lib/utils';
import { ROUTES } from '@lib/routes';
import type { Bike, Location } from '@shared/types/bike';
import { useFormFields } from '@hooks/useFormFields';
import { validateWithSchema } from '@lib/forms';

const MAX_RENTAL_DAYS = 7;
const INSURANCE_PRICE_PER_DAY = 500; // $5.00 per day in cents

const bookingSchema = z.object({
  startDate: z.string().min(1, 'Start date is required'),
  startTime: z.string().min(1, 'Start time is required'),
  endDate: z.string().min(1, 'End date is required'),
  endTime: z.string().min(1, 'End time is required'),
  insurance: z.boolean(),
  emergencyContactName: z.string().min(2, 'Emergency contact name is required'),
  emergencyContactPhone: z.string().min(10, 'Emergency contact phone is required'),
});

type BookingFormData = z.infer<typeof bookingSchema>;

export function BookingPage(): JSX.Element {
  const navigate = useNavigate();
  const locationState = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [bike, setBike] = useState<Bike | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [isLoadingBike, setIsLoadingBike] = useState(true);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  const createBooking = useCreateBooking();
  const checkAvailability = useCheckAvailability();

  // Get bikeId from location state (passed from bike detail page)
  const bikeId = (locationState.state as { bikeId?: string })?.bikeId;

  const {
    values,
    handleChange,
    errors: fieldErrors,
    setErrors,
    setFieldValue,
  } = useFormFields<BookingFormData>({
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    insurance: false,
    emergencyContactName: '',
    emergencyContactPhone: '',
  });

  const startDate = values.startDate;
  const endDate = values.endDate;
  const insurance = values.insurance;

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN, { state: { from: locationState } });
    }
  }, [isAuthenticated, navigate, locationState]);

  // Load bike and location data
  useEffect(() => {
    const loadData = async () => {
      if (!bikeId) {
        // If no bikeId, redirect to search
        navigate(ROUTES.CUSTOMER.SEARCH);
        return;
      }

      try {
        setIsLoadingBike(true);
        const bikeData = await getBike(bikeId);
        if (!bikeData) {
          navigate(ROUTES.CUSTOMER.SEARCH);
          return;
        }

        setBike(bikeData);

        // Load location data
        if (bikeData.locationId) {
          const locationData = await getLocation(bikeData.locationId);
          if (locationData) {
            setLocation(locationData);
          }
        }
      } catch (error) {
        console.error('Failed to load bike:', error);
        navigate(ROUTES.CUSTOMER.SEARCH);
      } finally {
        setIsLoadingBike(false);
      }
    };

    if (isAuthenticated && bikeId) {
      loadData();
    }
  }, [bikeId, isAuthenticated, navigate]);

  // Calculate rental duration
  const calculateDuration = (): { hours: number; days: number } => {
    if (!values.startDate || !values.endDate || !values.startTime || !values.endTime) {
      return { hours: 0, days: 0 };
    }

    const start = new Date(`${values.startDate}T${values.startTime}`);
    const end = new Date(`${values.endDate}T${values.endTime}`);
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffHours / 24);
    const hours = diffHours % 24;

    return { hours, days };
  };

  // Calculate pricing
  const calculatePricing = (): {
    basePrice: number;
    insurancePrice: number;
    totalPrice: number;
    rentalType: 'half-day' | 'full-day' | 'multi-day';
    numberOfDays?: number;
  } => {
    const { hours, days } = calculateDuration();
    const totalHours = days * 24 + hours;

    if (!bike) {
      return { basePrice: 0, insurancePrice: 0, totalPrice: 0, rentalType: 'half-day' };
    }

    let basePrice = 0;
    let rentalType: 'half-day' | 'full-day' | 'multi-day' = 'half-day';
    let numberOfDays: number | undefined;

    if (days > 0) {
      // Multi-day rental
      rentalType = 'multi-day';
      numberOfDays = days + (hours > 0 ? 1 : 0);
      basePrice = numberOfDays * bike.fullDayRate;
      
      // Apply discounts
      if (numberOfDays >= 7 && bike.weekDiscount) {
        basePrice = Math.floor(basePrice * (1 - bike.weekDiscount / 100));
      } else if (numberOfDays >= 3 && bike.threeDayDiscount) {
        basePrice = Math.floor(basePrice * (1 - bike.threeDayDiscount / 100));
      } else if (numberOfDays >= 2 && bike.twoDayDiscount) {
        basePrice = Math.floor(basePrice * (1 - bike.twoDayDiscount / 100));
      }
    } else if (totalHours >= 12) {
      // Full day (12+ hours)
      rentalType = 'full-day';
      basePrice = bike.fullDayRate;
    } else {
      // Half day
      rentalType = 'half-day';
      basePrice = bike.halfDayRate;
    }

    const insurancePrice = insurance && days > 0 ? days * INSURANCE_PRICE_PER_DAY : 0;
    const totalPrice = basePrice + insurancePrice;

    return { basePrice, insurancePrice, totalPrice, rentalType, numberOfDays };
  };

  const { basePrice, insurancePrice, totalPrice, rentalType, numberOfDays } = calculatePricing();
  const { hours, days } = calculateDuration();

  // Check availability when dates are selected
  const startTime = values.startDate && values.startTime ? new Date(`${values.startDate}T${values.startTime}`) : null;
  const endTime = values.endDate && values.endTime ? new Date(`${values.endDate}T${values.endTime}`) : null;

  const availability = useBikeAvailability(bikeId || null, startTime, endTime, !!startTime && !!endTime);

  const handleNext = (): void => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = (): void => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const submitBooking = async (data: BookingFormData): Promise<void> => {
    if (!bike || !user || !bikeId || !bike.locationId) {
      return;
    }

    try {
      setAvailabilityError(null);

      const startDateTime = new Date(`${data.startDate}T${data.startTime}`);
      const endDateTime = new Date(`${data.endDate}T${data.endTime}`);

      // Check availability one more time before creating
      const availabilityCheck = await checkAvailability.mutateAsync({
        bikeId,
        startTime: startDateTime,
        endTime: endDateTime,
      });

      if (!availabilityCheck.available) {
        setAvailabilityError('This bike is no longer available for the selected time period. Please choose different dates.');
        return;
      }

      // Create booking
      const { bookingId } = await createBooking.mutateAsync({
        bikeId,
        locationId: bike.locationId,
        startTime: startDateTime,
        endTime: endDateTime,
        rentalType,
        numberOfDays,
        halfDayRate: bike.halfDayRate,
        fullDayRate: bike.fullDayRate,
        totalPrice,
        insuranceIncluded: data.insurance,
        insurancePrice: data.insurance ? insurancePrice : undefined,
        emergencyContactName: data.emergencyContactName,
        emergencyContactPhone: data.emergencyContactPhone,
      });

      // Navigate to payment/confirmation
      navigate(ROUTES.CUSTOMER.BOOKING_CONFIRM, {
        state: {
          bookingId,
          bikeId,
          bikeName: bike.name,
          locationName: location?.name || 'Location',
          startDate: data.startDate,
          endDate: data.endDate,
          startTime: data.startTime,
          endTime: data.endTime,
          totalPrice,
          insuranceIncluded: data.insurance,
        },
      });
    } catch (error: unknown) {
      console.error('Booking failed:', error);
      if (error instanceof Error) {
        setAvailabilityError(error.message || 'Failed to create booking. Please try again.');
      } else {
        setAvailabilityError('Failed to create booking. Please try again.');
      }
    }
  };

  const minDate = format(new Date(), 'yyyy-MM-dd');
  const maxDate = format(addDays(new Date(), MAX_RENTAL_DAYS), 'yyyy-MM-dd');

  if (isLoadingBike) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!bike) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400 mb-4">Bike not found</p>
              <Button onClick={() => navigate(ROUTES.CUSTOMER.SEARCH)}>Search Bikes</Button>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const validation = validateWithSchema(bookingSchema, values);
    if (!validation.success) {
      setErrors(validation.errors);
      return;
    }
    await submitBooking(validation.data);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Availability Warning */}
          {availabilityError && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">{availabilityError}</p>
            </div>
          )}

          {availability.data && !availability.data.available && (
            <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                This bike may not be available for the selected time period. Please verify availability before confirming.
              </p>
            </div>
          )}
          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                      currentStep >= step
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-gray-300 dark:border-gray-600 text-gray-400'
                    }`}
                  >
                    {step}
                  </div>
                  {step < 3 && (
                    <div
                      className={`flex-1 h-1 mx-2 ${
                        currentStep > step ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-600 dark:text-gray-400">
              <span>Date & Time</span>
              <span>Details</span>
              <span>Review</span>
            </div>
          </div>

          <form onSubmit={handleFormSubmit}>
            {/* Step 1: Date & Time */}
            {currentStep === 1 && (
              <Card>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Select Date & Time
                </h2>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Input
                      label="Start Date"
                      type="date"
                      name="startDate"
                      value={values.startDate}
                      min={minDate}
                      max={maxDate}
                      onChange={handleChange}
                      error={fieldErrors.startDate}
                    />
                    <Input
                      label="Start Time"
                      type="time"
                      name="startTime"
                      value={values.startTime}
                      className="mt-4"
                      onChange={handleChange}
                      error={fieldErrors.startTime}
                    />
                  </div>
                  <div>
                    <Input
                      label="End Date"
                      type="date"
                      name="endDate"
                      value={values.endDate}
                      min={startDate || minDate}
                      max={maxDate}
                      onChange={handleChange}
                      error={fieldErrors.endDate}
                    />
                    <Input
                      label="End Time"
                      type="time"
                      name="endTime"
                      value={values.endTime}
                      className="mt-4"
                      onChange={handleChange}
                      error={fieldErrors.endTime}
                    />
                  </div>
                </div>

                {days > MAX_RENTAL_DAYS && (
                  <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      Maximum rental period is {MAX_RENTAL_DAYS} days
                    </p>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button type="button" onClick={handleNext} disabled={!startDate || !endDate}>
                    Next
                  </Button>
                </div>
              </Card>
            )}

            {/* Step 2: Details */}
            {currentStep === 2 && (
              <Card>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Additional Details
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={insurance}
                        onChange={(e) => setFieldValue('insurance', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <span className="text-gray-900 dark:text-white font-medium">
                          Add Insurance Coverage
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {formatCurrency(INSURANCE_PRICE_PER_DAY)} per day - Protects against
                          damage and theft
                        </p>
                      </div>
                    </label>
                  </div>

                  <Input
                    label="Emergency Contact Name"
                    name="emergencyContactName"
                    value={values.emergencyContactName}
                    onChange={handleChange}
                    error={fieldErrors.emergencyContactName}
                  />
                  <Input
                    label="Emergency Contact Phone"
                    type="tel"
                    name="emergencyContactPhone"
                    value={values.emergencyContactPhone}
                    onChange={handleChange}
                    error={fieldErrors.emergencyContactPhone}
                  />
                </div>

                <div className="flex justify-between">
                  <Button type="button" variant="outline" onClick={handleBack}>
                    Back
                  </Button>
                  <Button type="button" onClick={handleNext}>Next</Button>
                </div>
              </Card>
            )}

            {/* Step 3: Review & Confirm */}
            {currentStep === 3 && (
              <div className="grid md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <Card>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                      Review Your Booking
                    </h2>

                    {/* Bike Summary */}
                    <div className="flex gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                      {bike.imageUrls && bike.imageUrls.length > 0 && (
                        <img
                          src={bike.imageUrls[0]}
                          alt={bike.name}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {bike.name}
                        </h3>
                        {location && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Location: {location.name}
                          </p>
                        )}
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Rental Period: {days > 0 && `${days} day${days !== 1 ? 's' : ''} `}
                          {hours > 0 && `${hours} hour${hours !== 1 ? 's' : ''}`}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {startDate && endDate && (
                            <>
                              {format(new Date(`${values.startDate}T${values.startTime}`), 'MMM d, yyyy h:mm a')} -{' '}
                              {format(new Date(`${values.endDate}T${values.endTime}`), 'MMM d, yyyy h:mm a')}
                            </>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Pricing Breakdown */}
                    <div className="space-y-2 mb-6">
                      <div className="flex justify-between text-gray-700 dark:text-gray-300">
                        <span>Base Rental</span>
                        <span>{formatCurrency(basePrice)}</span>
                      </div>
                      {insurance && (
                        <div className="flex justify-between text-gray-700 dark:text-gray-300">
                          <span>Insurance ({days} day{days !== 1 ? 's' : ''})</span>
                          <span>{formatCurrency(insurancePrice)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white pt-4 border-t border-gray-200 dark:border-gray-700">
                        <span>Total</span>
                        <span>{formatCurrency(totalPrice)}</span>
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <Button type="button" variant="outline" onClick={handleBack}>
                        Back
                      </Button>
                      <Button
                        type="submit"
                        isLoading={createBooking.isPending || checkAvailability.isPending}
                        disabled={!availability.data?.available && !!startTime && !!endTime}
                      >
                        Confirm & Pay
                      </Button>
                    </div>
                  </Card>
                </div>

                {/* Summary Sidebar */}
                <div>
                  <Card>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Booking Summary
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Bike:</span>
                        <p className="text-gray-900 dark:text-white font-medium">{bike.name}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Duration:</span>
                        <p className="text-gray-900 dark:text-white font-medium">
                          {days > 0 && `${days} day${days !== 1 ? 's' : ''} `}
                          {hours > 0 && `${hours} hour${hours !== 1 ? 's' : ''}`}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Insurance:</span>
                        <p className="text-gray-900 dark:text-white font-medium">
                          {insurance ? 'Included' : 'Not included'}
                        </p>
                      </div>
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Total:</span>
                          <span className="text-lg font-bold text-gray-900 dark:text-white">
                            {formatCurrency(totalPrice)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </Layout>
  );
}

